// クラブ用 WHEP プレイヤー。
// 各スピーカーから空間音響で配信音を鳴らし、ゾーン/距離/群衆エフェクトを掛ける。
//
// パンニング設計:
//   - PannerNode(HRTF) は片耳完全消失を招くため使わない
//   - 代わりに StereoPannerNode (pan を ±PAN_LIMIT に clamp) + 手動距離 Gain で構成
//   - 距離 Gain は inverse rolloff モデル: REF / (REF + d * ROLLOFF)

import type { ClubGraph } from "./audio-graph";
import { type Shape, MAP } from "./map";
import { enableOpusStereo } from "../whep-sdp";

interface LiveUrlResponse {
  whepUrl: string;
  isLive: boolean;
}

interface SpeakerNode {
  id: string;
  x: number;
  y: number;
  /** 距離による propagation delay (= 早期反射風の時間差)。
   *  distGain の前段に挟む。プレイヤー位置から speaker までの距離 px を
   *  world 1px=5cm / 音速 343m/s と仮定して秒に換算 (= d / 6860 sec)。
   *  delayTime は updatePlayerPos で `setTargetAtTime` 平滑追従する。 */
  delay: DelayNode;
  /** 距離による gain (inverse rolloff) */
  distGain: GainNode;
  /** 左右パン (壁固定 / clamped)。buildSpeakers で speaker の world x 位置から
   *  1 度だけ計算して set されている。updatePlayerPos では触らない。 */
  stereoPan: StereoPannerNode;
  /** 距離吸収用 lowpass。airAbsorption=false なら null */
  lowpass: BiquadFilterNode | null;
  /**
   * このスピーカーに送る入力ノード (= attachAudioTrack から繋ぐ口)。
   * speaker.id が "L"/"R" のときは ChannelSplitter の片 channel が、
   * それ以外 (中央/サブなど) のときは delay そのものが入る。
   * delay → distGain → stereoPan → (lowpass) → musicBus の順で配線済み。
   */
  input: AudioNode;
  /** "L" / "R" / null (= 両 channel ミックス) */
  channel: "L" | "R" | null;
}

/** 天井 4 発ツイーター (= 高域専用、HRTF を避けて equalpower PannerNode で「面で
 *  降り注ぐ」体感を作る独立 graph)。WHEP source upmix から直接 branch して
 *  highpass + PannerNode → musicBus に流す。speakerNodes 側の acousticCenter
 *  / distGain / lowpass モデルとは完全に独立。 */
interface CeilingTweeter {
  /** 高域だけ band split (= 2.5kHz 以上)。中低域は speakerNodes 側だけが受け持つ */
  highpass: BiquadFilterNode;
  /** 指向性つき空間化 (equalpower)。orientation は東/西/南/北 のいずれか固定 */
  panner: PannerNode;
}

/** 天井ツイーターの仮想高さ (= z, Phaser 2D 座標と同単位 px)。
 *  音速ベースの距離 delay を加味するほどの距離は無いが、PannerNode の
 *  distanceModel/refDistance に直接効く。 */
const TWEETER_Z = 300;
/** 高域 band split のクロスオーバー周波数 (Hz)。 */
const TWEETER_HIGHPASS_HZ = 2500;
/** Phaser 2D 座標は y が下方向。Web Audio listener / panner の y を Phaser y と
 *  そのまま 1:1 で対応させ、forward を `(0, -1, 0)` 固定 = Phaser の上 (= 画面奥) を
 *  「北」として扱う。scene.ts には向き概念がまだ無いのでこれで十分。 */
const LISTENER_FORWARD: readonly [number, number, number] = [0, -1, 0];
const LISTENER_UP: readonly [number, number, number] = [0, 0, 1];

/** 距離 delay は廃止 (= 0 固定)。実音速で計算しても 1/4 に縮めても、X 軸移動時に
 *  L / R speaker の delay 差が動的に変わって comb filter (= フランジャー風の
 *  位相干渉) で音色が「ぐにゃっと揺れる」体感を残してしまう。
 *  クラブの実体験は **どこにいてもほぼ一様に大音圧が降り注ぐ** ので、距離 delay の
 *  物理シミュレーションは聴感上のデメリットの方が大きい。DelayNode 自体は残しておくが
 *  常に delayTime=0 で通過させる (= 配線変更を最小に保って後で再有効化しやすい)。
 *  音場の方向感は **distGain + StereoPan + 距離吸収 lowpass** の 3 要素で十分作れる。 */
const DELAY_PX_PER_SEC = Infinity;
const DELAY_MAX_SEC = 0;
/** DelayNode コンストラクタの maxDelayTime (= 0 だとコンストラクタが拒否する実装も
 *  あるため最低 1ms 残す。実 delayTime は常に 0 にセットするので audio path には影響なし)。 */
const DELAY_NODE_MAX_SEC = 0.001;

/** 距離 gain falloff の目標は「**ライン・アレイ風のフラット SPL 分布**」。
 *  実クラブの PA は audience を狙って射出された line-array で、フロア中央〜端で
 *  ほぼ均等な音圧。点音源 inverse-square (= 6dB/距離倍) ではなく 3dB/距離倍に
 *  寄せたい。inverse rolloff 式 `REF / (REF + d*ROLLOFF)` は ROLLOFF と REF を
 *  両方緩めれば near-flat に近づく。
 *
 *  旧: REF=200, ROLLOFF=1.2 → dist 1000 で 0.143 (clamp 0.45) = 端と中央で -7dB
 *  新: REF=500, ROLLOFF=0.5, MIN_DIST_GAIN=0.7
 *    dist 1000 で 500/1000 = 0.5 (clamp 0.7) → 端で -3dB
 *    dist 100 で 500/550 = 0.909  → 近くで -0.8dB
 *    フロア全域 0.7〜0.9 (= 約 -3dB の窓) に圧縮。「近 speaker 強調」は残しつつ
 *    遠 speaker も埋もれない。 */
const REF_DISTANCE = 500;
const ROLLOFF = 0.5;
/** ラインアレイの想定 SPL 範囲下限。`/clip_PURE` での 1.0 との差は約 -3dB。 */
const MIN_DIST_GAIN = 0.7;
/** パン上限。片耳完全消失 (= |pan|=1.0) を避けつつ、可能な限り左右に振る。
 *  StereoPannerNode は equal-power pan law なので |pan|=0.85 で逆チャネルは
 *  -16dB ≒ 6% 程度。「片側 speaker のすぐ近くで踊ってる」明確な体感を作る。 */
const PAN_LIMIT = 0.85;
const PARAM_TIME_CONST = 0.05;
/** zone audio の player 位置によるブレンド: 距離 ZONE_BLEND_SCALE_PX (px) で
 *  重みが exp(-1) ≒ 37% に減衰する。小さいほど境界が「シャープ」、大きいほど
 *  「フワッと混ざる」体感。80px ≒ 1 歩分強で、ドアを跨ぐ 1-2 秒の間に音場が
 *  なめらかに切り替わる丁度よい範囲 (= 旧 discrete 切替 + 600ms ramp の代替)。 */
const ZONE_BLEND_SCALE_PX = 80;
/** ブレンド後パラメータを実際に Web Audio に流すときの setTargetAtTime の τ。
 *  毎フレーム呼ばれるため、param 自体は「現在の重み付き平均」に向かって
 *  滑らかに追従する。τ=0.15s で人間の聴感的にスムーズ + zipper noise なし。 */
const ZONE_BLEND_TIME_CONST = 0.15;

/** Club Acoustic Simulator の位置依存パラメータ (subPresence / hfReflection /
 *  cornerBass) の setTargetAtTime τ。Zone blend (= τ=0.15s) より少し速めに、
 *  キャラ移動でじわっと音場が変わる体感を作る。zipper / click にはならない。 */
const CE_PARAM_TIME_CONST = 0.1;

/** `pc.connectionState === "disconnected"` を `failed` に escalate するまでの
 *  grace period。WebKit (iOS) は transient な disconnected を頻発するため、
 *  即座に failed 扱いするとユーザに「📡 接続失敗」が連続表示される。
 *  ICE restart 復旧の余地を与えるために 3 秒 待ってから判定する。 */
const DISCONNECTED_GRACE_MS = 3_000;
/** WHIP / WHEP の `connect()` が失敗 (= `failed` 状態) になった後、自動で
 *  再試行する間隔 (ms)。配列を順に消費し、末尾値で steady-state retry を続ける。
 *  exponential backoff: 5s → 15s → 60s → 60s → ... */
const RECONNECT_BACKOFF_MS: ReadonlyArray<number> = [5_000, 15_000, 60_000];

export class Live {
  private audioCtx: AudioContext;
  private graph: ClubGraph;
  /** Club Acoustic 位置計算用の静的データキャッシュ (= updatePlayerPos が 60Hz で
   *  呼ばれるので、毎フレーム MAP.zones.find / MAP.decorations フィルタしない)。 */
  private floorRect: { x: number; y: number; w: number; h: number } | null = null;
  private subPositions: ReadonlyArray<{ x: number; y: number }> = [];
  private mainSpeakers: ReadonlyArray<{ x: number; y: number }> = [];
  private pc: RTCPeerConnection | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  /** mono source (OBS が 1ch で出している場合) を 2ch に upmix する Gain。
   *  ChannelSplitterNode は仕様上、mono 入力に対して output[1] を silent にするため、
   *  splitter の前段で必ず 2ch に揃える (= channelInterpretation: "speakers" で L=R 複製)。 */
  private sourceUpmix: GainNode | null = null;
  /** WHEP source を L/R 2ch に分割するスプリッタ。attach のたびに作り直す */
  private channelSplitter: ChannelSplitterNode | null = null;
  /** L/R 以外のスピーカー (中央など) 向けに 2ch を 1ch にまとめる merger */
  private monoMerger: ChannelMergerNode | null = null;
  private speakerNodes: SpeakerNode[] = [];
  /** 天井ツイーター graph (= speakerNodes と独立)。WHEP source upmix から
   *  直接 fan-out して highpass + PannerNode 4 発で「面で降り注ぐ」体感を作る。 */
  private ceilingTweeters: CeilingTweeter[] = [];
  /** 4 panner の合算出力を 0.25 ゲインで集約してから musicBus に流すバス
   *  (= 同位置 panner stack の +12dB peak を audio DSP HIGH 対策で 0dB に落とす)。 */
  private ceilingTweetersBus: GainNode | null = null;
  /** 天井ツイーター graph の入り口 (= source upmix → tweeterIn → 各 PannerNode)。
   *  attach のたびに作り直す。close() で disconnect する。 */
  private tweeterIn: GainNode | null = null;
  /** audio listener の forward / up を 1 度だけセットしたかフラグ。
   *  AudioListener の `forwardX` などは AudioParam なので setValueAtTime で書く。 */
  private listenerOriented = false;
  private playerX = 0;
  private playerY = 0;
  private crowdCount = 0;
  private connected = false;
  /** 直近の updateZoneBlend 引数。同じ (x, y) で再呼び出しされたら
   *  全部 skip する (= プレイヤー静止時の AudioParam event spam を防ぐ)。
   *  初期値 NaN で「まだ計算してない」を表現。 */
  private lastBlendX = Number.NaN;
  private lastBlendY = Number.NaN;
  /** sweetspot 円形領域の中心ほど 1.0、縁で 0 の連続値。scene 側が毎フレーム計算して
   *  `setSweetspotStrength` で渡す。distGain / lowpass / crowdLowShelf を strength で
   *  lerp して、PURE に近づくほど音色が完全クリアになる体感を作る。
   *  voice bus には影響させない (= 声は引き続き空間化される)。 */
  private sweetspotStrength = 0;
  // attachAudioTrack で作る <audio> はクラスフィールドに保持する。
  // 一部ブラウザ (iOS Safari / Chrome の特定バージョン) はローカル変数のままだと
  // GC されて WebRTC のオーディオが無音化する不具合が観察されている。
  private dummyAudioEl: HTMLAudioElement | null = null;
  /** WHEP video track を 1ch だけ取り出してまとめた MediaStream。scene 側で
   *  Phaser.GameObjects.Video.loadMediaStream() に渡してクラブ内スクリーンに映す。 */
  private currentVideoStream: MediaStream | null = null;
  /** `pc.connectionState === "disconnected"` を grace 監視するタイマー。
   *  grace 中に `connected` に戻ったらキャンセル、満了したら `failed` に escalate する。 */
  private disconnectedGraceTimer: ReturnType<typeof setTimeout> | null = null;
  /** auto retry のタイマー。`close()` でキャンセルされる。 */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** 連続失敗回数。`RECONNECT_BACKOFF_MS` のインデックスとして使う。
   *  `connected` で 0 にリセット。 */
  private retryAttempts = 0;
  /** ユーザ意図で停止された状態 (= 外部から `close()` が呼ばれた)。
   *  true の間は auto retry を起動しない。`connect()` が呼ばれると false に戻る。 */
  private stopped = false;

  // 接続状態の購読 (UI からフックする)
  //   - "unstable": pc.connectionState === "disconnected" の grace 期間中。
  //     ICE restart 復旧の余地がある transient 状態として "failed" とは区別する。
  onStatus: ((state: "idle" | "connecting" | "connected" | "unstable" | "failed") => void) | null = null;
  /**
   * <audio> の play() が autoplay policy / 省電力モードなどで reject されたときの通知。
   * HUD などで「ライブ音声が止まっています」を出すフック。
   */
  onAudioError: ((kind: "live") => void) | null = null;
  /** WHEP video track が来た瞬間に呼ばれる callback。同じ stream を複数の
   *  Phaser.GameObjects.Video に loadMediaStream して並列描画するために使う。 */
  onVideoStream: ((stream: MediaStream) => void) | null = null;

  constructor(audioCtx: AudioContext, graph: ClubGraph) {
    this.audioCtx = audioCtx;
    this.graph = graph;
    this.buildSpeakers();

    // Club Acoustic 位置計算用の静的データ (= MAP は immutable) を 1 度だけ解決して
    // updatePlayerPos の毎フレーム linear search を防ぐ (SRE review low 対応)。
    const floor = MAP.zones.find((z) => z.name === "floor");
    if (floor && floor.shape.type === "rect") {
      this.floorRect = { x: floor.shape.x, y: floor.shape.y, w: floor.shape.w, h: floor.shape.h };
    }
    this.subPositions = MAP.decorations
      .filter((d) => d.type === "sub_speaker")
      .map((d) => ({ x: d.x, y: d.y }));
    this.mainSpeakers = MAP.speakers
      .filter((sp) => sp.visible !== false)
      .map((sp) => ({ x: sp.x, y: sp.y }));
  }

  isConnected(): boolean {
    return this.connected;
  }

  /** 配信に接続。失敗しても入室は続行できるよう、例外を握る形。
   *  `failed` で終わった場合は `RECONNECT_BACKOFF_MS` に従って自動 retry が走る。
   *  外部から `close()` が呼ばれていた場合は retry は走らない (= 明示停止優先)。 */
  async connect(): Promise<boolean> {
    // 既存の pc / sourceNode / dummyAudioEl を念のため掃除する。
    // connect() が複数回呼ばれても古い peer connection が leak しないように。
    this.cleanup();
    // ユーザ意図 / auto retry いずれの再接続でも停止フラグを解除する。
    this.stopped = false;
    this.onStatus?.("connecting");
    try {
      const live = await this.fetchLiveUrl();
      if (!live || !live.whepUrl) {
        this.handleConnectFailure("no_live_url");
        return false;
      }

      const pc = new RTCPeerConnection({ bundlePolicy: "max-bundle" });
      this.pc = pc;
      pc.addTransceiver("audio", { direction: "recvonly" });
      // 映像は使わないが Cloudflare Stream WHEP の SDP 形式に揃えるため宣言する
      pc.addTransceiver("video", { direction: "recvonly" });

      pc.ontrack = (event) => {
        if (event.track.kind === "audio") {
          this.attachAudioTrack(event.track);
        } else if (event.track.kind === "video") {
          this.attachVideoTrack(event.track);
        }
      };

      pc.onconnectionstatechange = () => {
        // **stale pc guard**: cleanup() で `this.pc = null` した後でも、古い pc は
        // close() 経由で `closed` イベントを発火する。closure は古い `pc` を握っているので、
        // ここで `this.pc !== pc` を弾かないと、新 connect() の最中に古い pc 由来の
        // handleConnectFailure() が走って retryAttempts が二重加算されたり、新接続中の
        // status を上書きしたりする (= reconnect 連発時の race)。
        if (this.pc !== pc) return;
        const state = pc.connectionState;
        if (state === "connected") {
          // 復旧: grace / retry timer をクリア、失敗カウントもリセット。
          this.connected = true;
          this.retryAttempts = 0;
          this.clearRetryTimer();
          this.clearDisconnectedGrace();
          this.onStatus?.("connected");
        } else if (state === "disconnected") {
          // transient (= ICE restart の余地あり)。即座に failed にせず grace で待つ。
          this.startDisconnectedGrace(pc, "conn");
        } else if (state === "failed" || state === "closed") {
          this.handleConnectFailure(`pc_${state}`);
        }
      };

      // **iceConnectionState 併用** (iOS Safari edge case):
      // WebKit には `connectionState` が "disconnected" のまま固まる (= 真の failure を
      // 取り逃す) / "connected" を打たない (= 復旧が捕まらない) という両方向の bug が
      // 報告されている。`iceConnectionState` を独立に監視して両方向 fallback する。
      //
      // ice "failed"               → ICE 接続性チェック完全失敗、transient ではない → 即 escalate。
      // ice "disconnected"         → packet loss 等で transient → connectionState と同じく grace。
      // ice "connected"/"completed" → 復旧。connectionState が打たれない iOS バグの対称形として、
      //                              grace timer をクリア + status を connected に戻す。
      //                              これで Gemini 指摘の「grace timer leak」「unstable stuck」が
      //                              両方解決する (= conn 側 handler が打たれなくても ice 側で復旧)。
      pc.oniceconnectionstatechange = () => {
        if (this.pc !== pc) return;
        const ice = pc.iceConnectionState;
        if (ice === "failed") {
          this.handleConnectFailure(`ice_failed_pc_${pc.connectionState}`);
        } else if (ice === "disconnected") {
          this.startDisconnectedGrace(pc, "ice");
        } else if (ice === "connected" || ice === "completed") {
          // 復旧: grace / retry timer をクリア。
          // connectionState 側の handler が "connected" を打たなくても (iOS バグ)、
          // ice 側だけで status を connected に戻して UI が "unstable" / "OFF" で
          // 固定されないようにする。
          this.clearDisconnectedGrace();
          if (!this.connected) {
            this.connected = true;
            this.retryAttempts = 0;
            this.clearRetryTimer();
            this.onStatus?.("connected");
          }
        }
      };

      const offer = await pc.createOffer();
      // Opus を **stereo** で受信するよう offer SDP に明示する (= デフォルトは mono)。
      // /club は ChannelSplitter で L/R を別 speaker に分配する設計なので、ここが
      // mono だと「左右で違う音が鳴ってる感」が成立せず空間音響が破綻する。
      // 詳細は ../whep-sdp.ts。
      offer.sdp = offer.sdp ? enableOpusStereo(offer.sdp) : offer.sdp;
      await pc.setLocalDescription(offer);
      await this.waitForIceGathering(pc);

      // close / failed で pc.localDescription が null になり得るので防御チェック
      if (!pc.localDescription) {
        this.handleConnectFailure("no_local_description");
        return false;
      }

      // body は **明示的に munge した offer.sdp** を使う (pc.localDescription.sdp は
      // 実装によって fmtp を再整形して stereo フラグを落とすことがある)。
      const res = await fetch(live.whepUrl, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp ?? pc.localDescription.sdp,
      });
      if (!res.ok) {
        // 401 / 403 は cookie 切れ / auth エラーで auto retry しても直らない。
        // stopped を立てて retry スケジュールを止め、ユーザにリロードさせる。
        if (res.status === 401 || res.status === 403) {
          this.stopped = true;
          this.connected = false;
          this.clearDisconnectedGrace();
          this.clearRetryTimer();
          this.onStatus?.("failed");
          console.info(`[club/live] whep ${res.status}, halt auto-retry (auth expired)`);
          return false;
        }
        this.handleConnectFailure(`whep_${res.status}`);
        return false;
      }
      // 防御的に answer 側にも同じ munging を掛ける (Cloudflare が応答 SDP で stereo
      // フラグを落とすパターンに備える)。
      const answerSdp = enableOpusStereo(await res.text());
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      return true;
    } catch (e) {
      console.warn("[club/live] connect failed", e);
      this.handleConnectFailure("exception");
      return false;
    }
  }

  /** 接続失敗の共通処理: status を `failed` に倒し、auto retry をスケジュールする。
   *  `stopped` (= 外部から `close()` 済み) ならば retry は走らない。 */
  private handleConnectFailure(reason: string): void {
    this.connected = false;
    this.clearDisconnectedGrace();
    this.onStatus?.("failed");
    if (this.stopped) return;
    const idx = Math.min(this.retryAttempts, RECONNECT_BACKOFF_MS.length - 1);
    const delay = RECONNECT_BACKOFF_MS[idx] ?? RECONNECT_BACKOFF_MS[RECONNECT_BACKOFF_MS.length - 1] ?? 60_000;
    this.retryAttempts++;
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      // 直前で外部 close() が走った場合は stopped が立っているので何もしない
      if (this.stopped) return;
      void this.connect();
    }, delay);
    console.info(`[club/live] reconnect scheduled in ${delay}ms (attempt ${this.retryAttempts}, reason=${reason})`);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private clearDisconnectedGrace(): void {
    if (this.disconnectedGraceTimer !== null) {
      clearTimeout(this.disconnectedGraceTimer);
      this.disconnectedGraceTimer = null;
    }
  }

  /** `connectionState === "disconnected"` または `iceConnectionState === "disconnected"`
   *  発火時に呼ばれる grace 開始 helper。両方から呼ばれても重複起動しないよう dedup する
   *  (= 既に grace 走行中なら何もしない)。
   *  grace 満了時に `connectionState` と `iceConnectionState` の両方をチェックして
   *  「真の失敗」(= どちらかが disconnected/failed/closed 圏内) なら escalate する。 */
  private startDisconnectedGrace(pc: RTCPeerConnection, reason: string): void {
    if (this.pc !== pc) return;
    if (this.disconnectedGraceTimer !== null) return; // 既に走っている
    this.onStatus?.("unstable");
    this.disconnectedGraceTimer = setTimeout(() => {
      this.disconnectedGraceTimer = null;
      // grace 中に新 connect() で `this.pc` が差し替わっている可能性。stale pc は無視。
      if (this.pc !== pc) return;
      const conn = pc.connectionState;
      const ice = pc.iceConnectionState;
      const connBad = conn === "disconnected" || conn === "failed" || conn === "closed";
      const iceBad = ice === "disconnected" || ice === "failed";
      if (connBad || iceBad) {
        this.handleConnectFailure(`${reason}_pc_${conn}_ice_${ice}`);
      }
    }, DISCONNECTED_GRACE_MS);
  }

  /** 自分の論理座標。各スピーカーの距離 gain と空気吸収 lowpass を更新する。
   *
   *  **pan は固定** (= buildSpeakers で speaker の壁位置から計算した値を 1 度だけ
   *  セットする)。実クラブと同じで L 壁 speaker は player がどこにいても **常に
   *  player の左耳**、R 壁 speaker は常に右耳に panned される。
   *  以前は `pan = (sp.x - player.x) / LATERAL_SCALE` で player 相対に panning して
   *  いたが、player がフロアの端に立つと「自分から見て中央〜後ろにある speaker が
   *  hard right に偏る」現象が出て、left/right が反転して感じる挙動になっていた。
   *  「speaker は壁に固定で player は頭を回さず DJ 方向を向いている」前提なら
   *  pan は player 位置に依らない方が自然。
   *
   *  **distance gain / 空気吸収 lowpass** は player から各 speaker までの距離で
   *  毎フレーム更新する。「近づくと音が大きい / 高音が出る」体感はそのまま。
   *
   *  **sweetspot ON**: distGain=1.0 + lowpass=maxHz (= bypass)、PURE エリアでは
   *  音場中心とは無関係にフルレンジで流れる。
   */
  updatePlayerPos(x: number, y: number): void {
    this.playerX = x;
    this.playerY = y;
    const t = this.audioCtx.currentTime;
    const maxHz = MAP.ambience.airAbsorptionMaxHz;
    const minHz = MAP.ambience.airAbsorptionMinHz;
    const refHzDist = MAP.ambience.airAbsorptionRef;
    const s = this.sweetspotStrength; // 0..1、PURE 中心 1.0、縁 0.0

    for (const sp of this.speakerNodes) {
      const dx = sp.x - x;
      const dyRaw = sp.y - y;
      // speaker 縦長スタックの上端は y=20 ぐらいまで伸びるので、プレイヤーが
      // sp.y より上にいる場合は dy 弱重み付け (= スタックの高い面に近い扱い)。
      const dy = dyRaw > 0 ? dyRaw * 0.3 : dyRaw;
      const dist = Math.hypot(dx, dy);

      // 通常 distGain = inverse rolloff、MIN_DIST_GAIN で下限保証。
      // 「スピーカーに近づくと音が大きい」体感が出る (重心モデル廃止)。
      const normalGain = Math.max(
        MIN_DIST_GAIN,
        REF_DISTANCE / (REF_DISTANCE + dist * ROLLOFF),
      );
      // PURE 強度で 1.0 に lerp。中心ほど speaker 距離無視でフル gain。
      const effGain = normalGain * (1 - s) + 1.0 * s;
      sp.distGain.gain.setTargetAtTime(effGain, t, PARAM_TIME_CONST);

      // 空気吸収 lowpass: 指数減衰 + sweetspot で maxHz に lerp
      if (sp.lowpass && MAP.ambience.airAbsorption) {
        const ratio = Math.pow(2, -dist / refHzDist);
        const normalHz = minHz + (maxHz - minHz) * ratio;
        const effHz = normalHz * (1 - s) + maxHz * s;
        sp.lowpass.frequency.setTargetAtTime(effHz, t, PARAM_TIME_CONST);
      }

      // 距離 delay (= 早期反射風)。max DELAY_MAX_SEC で clamp。sweetspot で 0 に lerp。
      const normalDelay = Math.min(DELAY_MAX_SEC, dist / DELAY_PX_PER_SEC);
      const effDelay = normalDelay * (1 - s);
      sp.delay.delayTime.setTargetAtTime(effDelay, t, PARAM_TIME_CONST);
    }

    // 天井ツイーター用の listener 追従 (= PannerNode の方向感のためにプレイヤー
    // 位置を 3D 座標として渡す)。Phaser 2D xy をそのまま Web Audio xy に流し、
    // z=0 (= 床) で固定。tweeter 側は z=TWEETER_Z (= 天井) で配置済み。
    const listener = this.audioCtx.listener;
    if (typeof listener.positionX !== "undefined") {
      listener.positionX.setTargetAtTime(x, t, PARAM_TIME_CONST);
      listener.positionY.setTargetAtTime(y, t, PARAM_TIME_CONST);
      listener.positionZ.setTargetAtTime(0, t, PARAM_TIME_CONST);
    } else {
      const legacy = listener as unknown as {
        setPosition?: (x: number, y: number, z: number) => void;
      };
      legacy.setPosition?.(x, y, 0);
    }

    // ゾーン audio を連続ブレンドで更新する (= 旧 discrete setActiveZone + 600ms ramp の代替)。
    // 各 zone の rect への距離から exp(-d / scale) で重みを計算 → 正規化 → 重み付き平均。
    // 結果として、player がフロア↔バー↔エントランス間を移動するときに音量 / lowpass /
    // reverb が**境界を跨いだ瞬間だけ動く**のではなく、近づくにつれて滑らかに floor 寄り /
    // bar 寄り / entrance 寄りに変化する。
    this.updateZoneBlend(x, y);

    // Club Acoustic Simulator の position-driven params を更新 (desktop only)。
    // mobile では graph.clubAcousticEnabled === null なので skip。
    this.updateClubAcousticPosition(x, y, t);
  }

  /** Club Acoustic Simulator のパラメータをプレイヤー位置で modulate。
   *  - subPresence: 全 sub の寄与の和 (= 複数 Bertha sub の coupling 模倣)。0..0.25
   *  - hfReflection: 壁に近い AND L/R PA stack から遠い時のみ強い (= booth 真ん前で
   *    reflection max になる不自然さを排除、Game programmer review medium 対応)
   *  - cornerBass: 4 隅全体への寄与の和。0..+5dB
   *  mobile では audio node 自体が graph に乗ってないので全て null check で skip。
   *  静的データ (floorRect / subPositions / mainSpeakers) は constructor でキャッシュ済。 */
  private updateClubAcousticPosition(x: number, y: number, t: number): void {
    if (!this.graph.clubAcousticEnabled) return; // mobile

    // (1) sub presence: 全 sub の寄与の **和** で計算 (= 実機 Long の Bertha 複数 sub
    //     coupling 模倣)。各 sub の寄与 = max(0, 1 - dist/400)、合計を 0..1 で clamp。
    //     dance floor 中央 (= 全 sub から ~等距離) でも複数 sub から少しずつ受ける。
    let subContrib = 0;
    for (const sp of this.subPositions) {
      const d = Math.hypot(sp.x - x, sp.y - y);
      subContrib += Math.max(0, 1 - d / 400);
    }
    const subProximity = Math.min(1, subContrib);
    if (this.graph.subPresenceBlend) {
      this.graph.subPresenceBlend.gain.setTargetAtTime(
        subProximity * 0.25,
        t,
        CE_PARAM_TIME_CONST,
      );
    }

    // (2) HF reflection: 「壁に近い」**かつ**「PA stack から遠い」時だけ強い。
    //     booth 真ん前 (= L/R PA 至近) では反射は出ないのが正しい (= 直接音が支配的)。
    //     wallNear * paFar の積で gating する (Game programmer review medium 対応)。
    let wallDist = Infinity;
    if (this.floorRect) {
      const { x: fx, y: fy, w: fw, h: fh } = this.floorRect;
      const insideX = x >= fx && x <= fx + fw;
      const insideY = y >= fy && y <= fy + fh;
      if (insideX && insideY) {
        wallDist = Math.min(x - fx, fx + fw - x, y - fy, fy + fh - y);
      } else {
        wallDist = 0;
      }
    }
    const wallNear = wallDist === Infinity ? 0 : Math.max(0, 1 - wallDist / 150);
    let paMinDist = Infinity;
    for (const sp of this.mainSpeakers) {
      const d = Math.hypot(sp.x - x, sp.y - y);
      if (d < paMinDist) paMinDist = d;
    }
    // PA から 600px 以上で 1 (= 反射支配)、150px で 0 (= 直接音支配)。
    const paFar = paMinDist === Infinity
      ? 1
      : Math.max(0, Math.min(1, (paMinDist - 150) / 450));
    if (this.graph.hfReflectionBlend) {
      this.graph.hfReflectionBlend.gain.setTargetAtTime(
        wallNear * paFar * 0.15,
        t,
        CE_PARAM_TIME_CONST,
      );
    }

    // (3) Corner bass mode: 4 隅全体への寄与の **和** (= sub と同じ pattern)。
    //     長辺中央 (= 一方の壁の中央) でも軽い boost が出て連続的になる。
    let cornerContrib = 0;
    if (this.floorRect) {
      const { x: fx, y: fy, w: fw, h: fh } = this.floorRect;
      const corners: Array<[number, number]> = [
        [fx, fy], [fx + fw, fy], [fx, fy + fh], [fx + fw, fy + fh],
      ];
      for (const [cx, cy] of corners) {
        const d = Math.hypot(cx - x, cy - y);
        cornerContrib += Math.max(0, 1 - d / 300);
      }
    }
    const cornerProximity = Math.min(1, cornerContrib);
    if (this.graph.cornerBassShelf) {
      this.graph.cornerBassShelf.gain.setTargetAtTime(
        cornerProximity * 5,
        t,
        CE_PARAM_TIME_CONST,
      );
    }
  }

  /** プレイヤー位置から全 zone の audio params を重み付き平均してブレンド。
   *  weight = exp(-distance / ZONE_BLEND_SCALE_PX)。zone 内部なら d=0 (= weight 1)。
   *  zone から離れるほど指数減衰で寄与が消える。境界を跨いでも snap せず、
   *  player の位置に応じて連続的に音場が変化する。
   *  `setTargetAtTime` で param を毎フレーム更新するが τ=0.15s で滑らかに追従する
   *  (= zipper noise / 急変なし)。
   *
   *  **perceptual interpolation**: 旧版は linear-space で `lowpassHz` / `gain` を
   *  平均していたが、人間の聴感は周波数 (= octaves) も振幅 (= dB) も対数的なので
   *  linear avg だと中間位置が「明るく / 大きく」聞こえすぎる。log/dB 空間で
   *  weighted average → exp/pow で線形に戻す方が perceptual midpoint と一致する。
   *  例: floor (18000Hz) と bar (700Hz) の 50/50 →
   *    linear: 9350Hz (= ほぼ「明るい」)
   *    log:    sqrt(700×18000) ≈ 3550Hz (= 「半分こもり」、自然)
   *  `reverbWet` は 0..1 の send 比率なので linear のまま (= 知覚も線形)。
   *
   *  **stationary skip**: 同じ (x, y) で再呼び出しされたら早期 return。
   *  プレイヤー静止時の AudioParam event spam (= 60Hz × 3 params の冗長書き込み) を防ぐ。 */
  private updateZoneBlend(x: number, y: number): void {
    if (x === this.lastBlendX && y === this.lastBlendY) return;
    this.lastBlendX = x;
    this.lastBlendY = y;

    let totalWeight = 0;
    let logHz = 0;
    let dbGain = 0;
    let reverbWet = 0;
    for (const zone of MAP.zones) {
      const d = distanceToRect(zone.shape, x, y);
      const w = Math.exp(-d / ZONE_BLEND_SCALE_PX);
      totalWeight += w;
      // log/dB 空間で weighted sum。後で /totalWeight + exp/pow で戻す。
      // gain が 0 だと log10 が -Infinity になるので Math.max で floor。
      // 0.001 (= -60dB) は完全無音相当で MAP 上の現実的下限を下回る安全値。
      logHz += Math.log(zone.audio.lowpassHz) * w;
      dbGain += (20 * Math.log10(Math.max(0.001, zone.audio.gain))) * w;
      reverbWet += zone.audio.reverbWet * w;
    }
    if (totalWeight === 0) return; // 全 zone から ~infinity 離れている (= ありえない)
    const lowpassHz = Math.exp(logHz / totalWeight);
    const gain = Math.pow(10, (dbGain / totalWeight) / 20);
    reverbWet /= totalWeight;

    const t = this.audioCtx.currentTime;
    this.graph.zoneGain.gain.setTargetAtTime(gain, t, ZONE_BLEND_TIME_CONST);
    this.graph.zoneFilter.frequency.setTargetAtTime(lowpassHz, t, ZONE_BLEND_TIME_CONST);
    this.graph.wetSend.gain.setTargetAtTime(reverbWet, t, ZONE_BLEND_TIME_CONST);
  }

  /** sweetspot 強度 (= PURE 円形領域中心ほど 1.0、縁で 0)。scene 側が毎フレーム
   *  プレイヤー位置と PURE 中心の距離から計算して呼ぶ。
   *  distGain / lowpass / delay / crowdLowShelf を strength で lerp して、
   *  「PURE に近づくほど DJ 音が完全クリアに」体感を作る。 */
  setSweetspotStrength(strength: number): void {
    const clamped = Math.max(0, Math.min(1, strength));
    if (Math.abs(clamped - this.sweetspotStrength) < 0.005) return; // 微小変化は無視
    this.sweetspotStrength = clamped;
    // 即時反映: 次の rAF を待たず、現在の player 座標 + crowd で再計算
    this.updatePlayerPos(this.playerX, this.playerY);
    this.setCrowdInternal();
  }

  /** 自分以外のピア数で低音を下げる (擬似クラウド吸収) */
  setCrowdCount(n: number): void {
    this.crowdCount = Math.max(0, n);
    this.setCrowdInternal();
  }

  /** crowd / sweetspot 状態から両 bus の crowdLowShelf を計算して反映。
   *  - music: `db × (1 - sweetspotStrength)` で sweetspot 中心ほど 0 に lerp
   *    (= 「DJ 音だけ群衆減衰 bypass」を連続値で表現)
   *  - voice: 常に `db` (= 声の空気感はそのまま、ユーザ要望)
   */
  private setCrowdInternal(): void {
    const db = Math.max(-12, -this.crowdCount * MAP.ambience.crowdLowCut.perPersonDb);
    const t = this.audioCtx.currentTime;
    const musicDb = db * (1 - this.sweetspotStrength);
    rampParam(this.graph.musicCrowdLowShelf.gain, musicDb, t, 0.2);
    rampParam(this.graph.voiceCrowdLowShelf.gain, db, t, 0.2);
  }

  getCrowdDb(): number {
    return -this.crowdCount * MAP.ambience.crowdLowCut.perPersonDb;
  }

  /** musicBus 直後の周波数分析 AnalyserNode を返す。reactive lighting (scene.ts) が
   *  毎フレーム `getByteFrequencyData` で読む用途。lighting OFF の間は呼ばれないので
   *  FFT は実行されない (= AnalyserNode の遅延評価)。 */
  getMusicAnalyser(): AnalyserNode {
    return this.graph.musicAnalyser;
  }

  /** 外部から明示的に停止する。auto retry も止める (= ユーザがページを去った想定)。
   *  内部の `connect()` リトライからは `cleanup()` を直接呼ぶので、ここで stopped を
   *  立てても問題ない。 */
  close(): void {
    this.stopped = true;
    this.cleanup();
  }

  /** 内部リソースだけ片付ける (= pc / audio nodes / timers / dummy audio / video stream)。
   *  `stopped` フラグは触らない。`connect()` の冒頭から呼ばれて古い pc を捨てる用途も
   *  兼ねるので、retry サイクルを止めない。 */
  private cleanup(): void {
    this.clearRetryTimer();
    this.clearDisconnectedGrace();
    try { this.pc?.close(); } catch { /* ignore */ }
    this.pc = null;
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch { /* ignore */ }
      this.sourceNode = null;
    }
    if (this.sourceUpmix) {
      try { this.sourceUpmix.disconnect(); } catch { /* ignore */ }
      this.sourceUpmix = null;
    }
    if (this.channelSplitter) {
      try { this.channelSplitter.disconnect(); } catch { /* ignore */ }
      this.channelSplitter = null;
    }
    if (this.monoMerger) {
      try { this.monoMerger.disconnect(); } catch { /* ignore */ }
      this.monoMerger = null;
    }
    if (this.tweeterIn) {
      try { this.tweeterIn.disconnect(); } catch { /* ignore */ }
      this.tweeterIn = null;
    }
    this.disposeCeilingTweeters();
    if (this.dummyAudioEl) {
      // srcObject = null まで明示しないと内部の MediaStream 参照が残り続ける
      try { this.dummyAudioEl.pause(); } catch { /* ignore */ }
      try { this.dummyAudioEl.srcObject = null; } catch { /* ignore */ }
      this.dummyAudioEl = null;
    }
    if (this.currentVideoStream) {
      for (const t of this.currentVideoStream.getTracks()) {
        try { t.stop(); } catch { /* ignore */ }
      }
      this.currentVideoStream = null;
    }
    this.connected = false;
  }

  /** scene 側で「自分が後から登録した onVideoStream に間に合わなかった」状況を救うため、
   *  現在の MediaStream を同期的に取り出せる getter。null = まだ video track が来ていない。 */
  getVideoStream(): MediaStream | null {
    return this.currentVideoStream;
  }

  // ─────────────────────────────────────

  private buildSpeakers(): void {
    for (const sp of MAP.speakers) {
      // 距離 delay (= 早期反射風)。delayTime は updatePlayerPos で設定。
      // maxDelayTime は clamp の DELAY_MAX_SEC より少し余裕を持たせる。
      // iOS Safari 14 系の `new DelayNode(...)` 互換性問題を避けるため factory 使用
      // (browser API HIGH 対応)。
      const delay = this.audioCtx.createDelay(DELAY_NODE_MAX_SEC);
      delay.delayTime.value = 0;

      const distGain = this.audioCtx.createGain();
      distGain.gain.value = 0; // attachAudioTrack 後の updatePlayerPos で正しい値に更新される

      // pan は **壁固定**: speaker の world x 位置から計算して 1 度だけセットする
      // (= updatePlayerPos では触らない)。実クラブと同じで「L 壁の speaker は player
      // がフロアのどこにいても常に player の左耳」挙動になる。
      // `(sp.x - center) / halfW` を [-1, +1] に正規化 → PAN_LIMIT で頭打ち。
      // 例: world.w=1600, center=800, halfW=800
      //  - L (x=70):   (70-800)/800   = -0.91 → clamp -0.85 (HARD LEFT)
      //  - BL (x=200): (200-800)/800  = -0.75 →            -0.75 (LEFT)
      //  - C (x=800):  (800-800)/800  = 0                   0    (CENTER)
      //  - BR (x=1400):(1400-800)/800 = +0.75 →           +0.75 (RIGHT)
      //  - R (x=1530): (1530-800)/800 = +0.91 → clamp +0.85 (HARD RIGHT)
      const centerX = MAP.world.w / 2;
      const halfW = MAP.world.w / 2;
      const rawFixedPan = (sp.x - centerX) / halfW;
      const fixedPan = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, rawFixedPan));
      const stereoPan = this.audioCtx.createStereoPanner();
      stereoPan.pan.value = fixedPan;

      let lowpass: BiquadFilterNode | null = null;
      if (MAP.ambience.airAbsorption) {
        lowpass = this.audioCtx.createBiquadFilter();
        lowpass.type = "lowpass";
        lowpass.frequency.value = MAP.ambience.airAbsorptionMaxHz;
        lowpass.Q.value = 0.7071;
      }

      // 配線: (input=delay) → distGain → stereoPan → (lowpass) → musicBus
      // input は attachAudioTrack 内で channel splitter / merger を介して接続される
      // ので、ここでは delay を入力ノードとして保持しておく (= 配信音は必ず
      // delay → distGain の順で通過する)。
      delay.connect(distGain);
      distGain.connect(stereoPan);
      if (lowpass) {
        stereoPan.connect(lowpass);
        lowpass.connect(this.graph.musicBus);
      } else {
        stereoPan.connect(this.graph.musicBus);
      }

      const channel: "L" | "R" | null = sp.id === "L" || sp.id === "R" ? sp.id : null;

      this.speakerNodes.push({
        id: sp.id,
        x: sp.x,
        y: sp.y,
        delay,
        distGain,
        stereoPan,
        lowpass,
        input: delay,
        channel,
      });
    }
  }

  /** 天井 4 発ツイーター graph (= 高域専用) を初期化する。speakerNodes と独立した
   *  並列経路で musicBus に合流。WHEP source の upmix から **branch** して
   *  highpass → PannerNode(equalpower) → musicBus に流す。
   *
   *  PannerNode は HRTF を避けて `panningModel: "equalpower"` を使い、
   *  `distanceModel: "inverse"` + `refDistance: 300` + `rolloffFactor: 0.6`、
   *  `coneInnerAngle: 120` / `coneOuterAngle: 240` / `coneOuterGain: 0.4` で
   *  「ピンポイント」を避けた面降り注ぎを再現する。orientation は東西南北
   *  4 方向で、4 個の総和が空間平均化されて「天井から降ってくる高音」を作る。
   *
   *  listener position は updatePlayerPos で毎回更新。forward/up は
   *  Phaser y (画面下が大) と Web Audio y を 1:1 対応させ、forward=(0,-1,0)
   *  固定 = Phaser の上 = 北。 */
  private buildCeilingTweeters(): void {
    // 重複起動防止 (= attachAudioTrack を 2 回呼ばれた場合に備え teardown も併用)
    this.disposeCeilingTweeters();

    // フロア重心 (= world 中心) を XY、Z=TWEETER_Z にして 4 発を同じ位置に置く。
    // 同位置の PannerNode 4 個でも orientation cone が違うので方向ごとに gain が
    // 変わり、結果として「面で降り注ぐ」音場になる。
    const cx = MAP.world.w / 2;
    const cy = MAP.world.h / 2;

    const orientations: ReadonlyArray<readonly [number, number, number]> = [
      [1, 0, 0],   // 東
      [-1, 0, 0],  // 西
      [0, 1, 0],   // 南 (= Phaser 座標で下方向)
      [0, -1, 0],  // 北 (= Phaser 座標で上方向)
    ];

    // 4 panner 同位置で full gain 出力すると合算 +12dB peak になる (audio DSP HIGH)。
    // 4 panner の出力を 1 つの GainNode (= gain 0.25) に集約してから musicBus に
    // 流すことで合算 0dB に落とす。tweetersBus は dispose で disconnect する。
    const tweetersBus = this.audioCtx.createGain();
    tweetersBus.gain.value = 0.25;
    tweetersBus.connect(this.graph.musicBus);
    this.ceilingTweetersBus = tweetersBus;

    for (const ori of orientations) {
      // iOS Safari 14 系は `new BiquadFilterNode(...)` も問題ないが、project の
      // 既存 pattern (= audioCtx.createXxx() factory) に合わせる。
      const highpass = this.audioCtx.createBiquadFilter();
      highpass.type = "highpass";
      highpass.frequency.value = TWEETER_HIGHPASS_HZ;
      highpass.Q.value = 0.7071;

      // iOS Safari 14 系で `new PannerNode(ctx, options)` が
      // `TypeError: undefined is not a constructor` を投げるので、factory + setter
      // 形式に統一 (browser API HIGH)。位置 / 向きは AudioParam ではなく
      // legacy `setPosition` / `setOrientation` を使う (= iOS Safari 14 互換、
      // 後方互換で modern ブラウザも動く)。
      const panner = this.audioCtx.createPanner();
      panner.panningModel = "equalpower";
      panner.distanceModel = "inverse";
      panner.refDistance = 300;
      panner.rolloffFactor = 0.6;
      panner.coneInnerAngle = 120;
      panner.coneOuterAngle = 240;
      panner.coneOuterGain = 0.4;
      panner.setPosition(cx, cy, TWEETER_Z);
      panner.setOrientation(ori[0], ori[1], ori[2]);

      highpass.connect(panner);
      panner.connect(tweetersBus);

      this.ceilingTweeters.push({ highpass, panner });
    }
  }

  /** 天井ツイーター nodes を全部 disconnect。tweeterIn は呼び出し側で別途処理。 */
  private disposeCeilingTweeters(): void {
    for (const tw of this.ceilingTweeters) {
      try { tw.panner.disconnect(); } catch { /* ignore */ }
      try { tw.highpass.disconnect(); } catch { /* ignore */ }
    }
    this.ceilingTweeters = [];
    if (this.ceilingTweetersBus) {
      try { this.ceilingTweetersBus.disconnect(); } catch { /* ignore */ }
      this.ceilingTweetersBus = null;
    }
  }

  /** AudioListener の forward / up を 1 度だけセット。scene.ts には向き概念が
   *  まだ無いので、Phaser y (画面下が大) と Web Audio y を 1:1 対応させて
   *  forward=(0,-1,0) で固定 = Phaser の上 = 「北」。 */
  private orientListenerOnce(): void {
    if (this.listenerOriented) return;
    const listener = this.audioCtx.listener;
    const t = this.audioCtx.currentTime;
    // forwardX/upX 系が無い古い Safari のため fallback を持つ。
    // 公式 spec では AudioParam だが、WebKit 一部バージョンは
    // setOrientation(fx, fy, fz, ux, uy, uz) のレガシー method を持つ。
    if (typeof listener.forwardX !== "undefined") {
      listener.forwardX.setValueAtTime(LISTENER_FORWARD[0], t);
      listener.forwardY.setValueAtTime(LISTENER_FORWARD[1], t);
      listener.forwardZ.setValueAtTime(LISTENER_FORWARD[2], t);
      listener.upX.setValueAtTime(LISTENER_UP[0], t);
      listener.upY.setValueAtTime(LISTENER_UP[1], t);
      listener.upZ.setValueAtTime(LISTENER_UP[2], t);
    } else {
      const legacy = listener as unknown as {
        setOrientation?: (fx: number, fy: number, fz: number, ux: number, uy: number, uz: number) => void;
      };
      legacy.setOrientation?.(
        LISTENER_FORWARD[0], LISTENER_FORWARD[1], LISTENER_FORWARD[2],
        LISTENER_UP[0], LISTENER_UP[1], LISTENER_UP[2],
      );
    }
    this.listenerOriented = true;
  }

  private attachAudioTrack(track: MediaStreamTrack): void {
    // WHEP renegotiation などで再 attach される場合に備えて、前回ぶんを掃除する。
    // 残しておくと <audio> + sourceNode が destination に繋がりっぱなしでリーク。
    if (this.dummyAudioEl) {
      try { this.dummyAudioEl.pause(); } catch { /* ignore */ }
      try { this.dummyAudioEl.srcObject = null; } catch { /* ignore */ }
      this.dummyAudioEl = null;
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch { /* ignore */ }
      this.sourceNode = null;
    }
    if (this.sourceUpmix) {
      try { this.sourceUpmix.disconnect(); } catch { /* ignore */ }
      this.sourceUpmix = null;
    }
    if (this.channelSplitter) {
      try { this.channelSplitter.disconnect(); } catch { /* ignore */ }
      this.channelSplitter = null;
    }
    if (this.monoMerger) {
      try { this.monoMerger.disconnect(); } catch { /* ignore */ }
      this.monoMerger = null;
    }
    if (this.tweeterIn) {
      try { this.tweeterIn.disconnect(); } catch { /* ignore */ }
      this.tweeterIn = null;
    }
    this.disposeCeilingTweeters();

    // 同じ track を 2 つの MediaStream に入れる実装は仕様的にグレー (一部実装で
    // バグの温床)。1 つだけ作って <audio> と createMediaStreamSource の両方で使い回す。
    const stream = new MediaStream([track]);

    // virtual-office と同じ回避: <audio> にも srcObject を流して mute 再生する。
    // class field に保持しないと GC されてオーディオが止まるブラウザがある。
    const audioEl = new Audio();
    audioEl.srcObject = stream;
    audioEl.muted = true;
    audioEl.autoplay = true;
    void audioEl.play().catch((err) => {
      // autoplay policy / 省電力モード等で reject されるケース。
      // 失敗時はサイレントに切らず、上位 (HUD) に通知してデバッグを楽にする。
      console.warn("[club/live] dummy audio play failed", err);
      this.onAudioError?.("live");
    });
    this.dummyAudioEl = audioEl;

    const source = this.audioCtx.createMediaStreamSource(stream);
    this.sourceNode = source;

    // 配信元 (OBS) が mono (1 channel) を出している場合、ChannelSplitterNode の
    // output[1] は仕様上 silent になる (= R speaker が永久に無音)。
    // GainNode を挟んで `channelCount = 2 / explicit / "speakers"` で強制 2ch 化する。
    // - mono → 2ch upmix: L=R に同じ信号が複製される (speakers layout 仕様)
    // - stereo → 2ch: そのまま (no-op)
    // これで OBS の channel layout に関わらず L/R 両方のスピーカーから音が出る。
    const upmix = this.audioCtx.createGain();
    upmix.channelCount = 2;
    upmix.channelCountMode = "explicit";
    upmix.channelInterpretation = "speakers";
    source.connect(upmix);
    this.sourceUpmix = upmix;

    // reactive lighting (scene.ts) 用の AnalyserNode は audio-graph で生成済み。
    // ここで sourceUpmix → musicAnalyser を繋ぐ (= musicBus tap ではなく、speaker
    // fan-out 前の生 source を見る = player 位置で band energy が動かない、全 player
    // で同じ lighting が走る共有体験を作る)。
    // reattach 時は上の cleanup で旧 upmix.disconnect() が走るので、analyser 側の
    // 入力経路も自動でクリアされる (重複接続にならない)。
    upmix.connect(this.graph.musicAnalyser);

    // WHEP の stereo mix を ChannelSplitterNode(2) で L/R に分割。
    // map.ts の speaker.id === "L" には L チャンネルだけ、"R" には R だけ送ると
    // 「クラブの PA 越し」感が出る。それ以外 (中央/サブ) のスピーカーには
    // ChannelMerger で L+R を再合成 (= mono mix) して送る。
    const splitter = this.audioCtx.createChannelSplitter(2);
    upmix.connect(splitter);
    this.channelSplitter = splitter;

    // 中央/サブ用の mono mix が必要なときだけ merger を作る
    let monoMerger: ChannelMergerNode | null = null;
    const needMono = this.speakerNodes.some((sp) => sp.channel === null);
    if (needMono) {
      monoMerger = this.audioCtx.createChannelMerger(2);
      splitter.connect(monoMerger, 0, 0);
      splitter.connect(monoMerger, 1, 1);
      this.monoMerger = monoMerger;
    }

    for (const sp of this.speakerNodes) {
      if (sp.channel === "L") {
        splitter.connect(sp.input, 0);
      } else if (sp.channel === "R") {
        splitter.connect(sp.input, 1);
      } else if (monoMerger) {
        monoMerger.connect(sp.input);
      }
    }

    // 天井ツイーター: upmix から直接 branch (= 既存 speakerNodes graph に影響を
    // 与えない独立並列経路)。tweeterIn は単純な GainNode で fan-out の根として
    // 使い、各 PannerNode の highpass にぶら下げる。
    this.buildCeilingTweeters();
    const tweeterIn = this.audioCtx.createGain();
    tweeterIn.gain.value = 1.0;
    upmix.connect(tweeterIn);
    for (const tw of this.ceilingTweeters) {
      tweeterIn.connect(tw.highpass);
    }
    this.tweeterIn = tweeterIn;

    // listener forward/up を 1 度だけ固定。position は updatePlayerPos で追従。
    this.orientListenerOnce();

    // 初期距離/パンを直ちに反映 (= プレイヤー位置はまだ初期値 0,0 のはずなので
    // scene.ts 側の最初の updatePlayerPos が走るまで音は出ない可能性がある。
    // ここで一度 updatePlayerPos を呼んで初期化しておく)
    this.updatePlayerPos(this.playerX, this.playerY);
  }

  /** WHEP video track を MediaStream にラップして scene 側へ通知する。
   *  音声は別経路 (= audio graph 経由でスピーカー空間化) で鳴らしているので、
   *  scene 側の `Phaser.GameObjects.Video.loadMediaStream(stream, ..., true)` で
   *  必ず noAudio = true を渡して二重再生を避けること。 */
  private attachVideoTrack(track: MediaStreamTrack): void {
    // 再 attach (= renegotiation) の場合は前 stream のトラックを停止して
    // ブラウザ側のリソースを解放する。
    if (this.currentVideoStream) {
      for (const t of this.currentVideoStream.getTracks()) {
        try { t.stop(); } catch { /* ignore */ }
      }
    }
    const stream = new MediaStream([track]);
    this.currentVideoStream = stream;
    this.onVideoStream?.(stream);
  }

  private waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
    if (pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise<void>((resolve) => {
      const cleanup = () => pc.removeEventListener("icegatheringstatechange", handler);
      const safety = setTimeout(() => {
        cleanup();
        resolve();
      }, 3000);
      const handler = () => {
        if (pc.iceGatheringState === "complete") {
          clearTimeout(safety);
          cleanup();
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", handler);
    });
  }

  private async fetchLiveUrl(): Promise<LiveUrlResponse | null> {
    try {
      const res = await fetch("/api/get-live-url", {
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) return null;
      return (await res.json()) as LiveUrlResponse;
    } catch {
      return null;
    }
  }
}

// linearRampToValueAtTime の前に setValueAtTime で「いまの値」を anchor する。
// 前の ramp が走っている途中だったり、最初の event が無かったりする状況でも
// 急なジャンプを起こさず滑らかに目標値へ向かう。
function rampParam(param: AudioParam, target: number, now: number, dur: number): void {
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(target, now + dur);
}

/** 点 (px, py) から axis-aligned rect への最短距離。内部 (= 含まれる) なら 0。
 *  updateZoneBlend で「player から各 zone への距離」を計算するのに使う。 */
function distanceToRect(rect: Shape, px: number, py: number): number {
  // x 軸: rect の外側にあれば符号付きで rect の左/右辺までの距離、内側なら 0。
  const dx = Math.max(rect.x - px, 0, px - (rect.x + rect.w));
  const dy = Math.max(rect.y - py, 0, py - (rect.y + rect.h));
  // 両軸 0 なら点は rect 内部 → 距離 0。
  return Math.hypot(dx, dy);
}
